import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Package, 
  ShoppingCart, 
  TrendingUp, 
  LogOut, 
  Edit, 
  Trash2, 
  Eye, 
  Upload,
  Download,
  Check,
  X,
  Calendar,
  Image
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { productService, orderService, reportService, uploadService } from '../services/api';
import toast from 'react-hot-toast';
import BannerManagement from './BannerManagement';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    short_description: '',
    long_description: '',
    price: '',
    discount_percentage: '',
    is_featured: false,
    category: '',
    sizes: [],
    colors: [],
    stock: {},
    images: []
  });

  const categories = ['camperas', 'camisetas', 'remeras', 'pantalones', 'ropa interior', 'buzos', 'medias', 'gorras', 'varios'];
  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'Talle unico'];
  const colors = ['Negro', 'Blanco', 'Azul', 'Rojo', 'Verde', 'Amarillo', 'Rosa', 'Morado', 'Gris', 'Beige'];

  useEffect(() => {
    console.log('🔍 AdminDashboard useEffect:', {authLoading, isAuthenticated});
    if (!authLoading && !isAuthenticated) {
      console.log('🚪 Redirecting to login');
      navigate('/admin/login');
      return;
    }
    if (!authLoading && isAuthenticated) {
      console.log('🚀 Calling fetchData');
      fetchData();
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchData = async () => {
    console.log('🔄 fetchData called');
    setLoading(true);
    
    try {
      console.log('📡 Fetching data...');
      
      // Fetch products
      console.log('   → Fetching products...');
      const productsRes = await productService.getAll(1, 100);
      console.log('   ✓ Products fetched:', productsRes.data);
      
      // Fetch orders
      console.log('   → Fetching orders...');
      const ordersRes = await orderService.getAll();
      console.log('   ✓ Orders fetched:', Array.isArray(ordersRes.data) ? ordersRes.data.length : ordersRes.data);
      
      // Fetch sales
      console.log('   → Fetching sales...');
      const salesRes = await reportService.getSales();
      console.log('   ✓ Sales fetched:', Array.isArray(salesRes.data) ? salesRes.data.length : salesRes.data);
      
      // Set state
      setProducts(productsRes.data.products || productsRes.data);
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      setSalesData(Array.isArray(salesRes.data) ? salesRes.data : []);
      
      console.log('✅ All data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading data:', error);
      toast.error('Error al cargar datos: ' + error.message);
    } finally {
      console.log('🏁 Setting loading to false');
      setLoading(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...productForm,
        price: parseFloat(productForm.price),
        discount_percentage: parseFloat(productForm.discount_percentage) || 0
      };
      
      if (editingProduct) {
        await productService.update(editingProduct.id, productData);
        toast.success('Producto actualizado exitosamente');
      } else {
        await productService.create(productData);
        toast.success('Producto creado exitosamente');
      }
      
      setShowProductModal(false);
      setEditingProduct(null);
      resetProductForm();
      fetchData();
    } catch (error) {
      toast.error('Error al guardar producto');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este producto?')) {
      try {
        await productService.delete(id);
        toast.success('Producto eliminado exitosamente');
        fetchData();
      } catch (error) {
        toast.error('Error al eliminar producto');
      }
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await orderService.updateStatus(orderId, status);
      toast.success('Estado de orden actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  // NUEVA FUNCIÓN PARA ELIMINAR ORDENES
  const handleDeleteOrder = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta orden? Esta acción es irreversible.')) {
      try {
        await orderService.delete(id);
        toast.success('Orden eliminada exitosamente');
        fetchData(); // Refrescar los datos después de la eliminación
      } catch (error) {
        toast.error('Error al eliminar orden');
      }
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    try {
      const uploadPromises = files.map(file => uploadService.uploadFile(file));
      const responses = await Promise.all(uploadPromises);
      
      const imageUrls = responses.map(res => res.data.url);
      setProductForm(prev => ({
        ...prev,
        images: [...prev.images, ...imageUrls]
      }));
      
      toast.success('Imágenes subidas exitosamente');
    } catch (error) {
      toast.error('Error al subir imágenes');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      short_description: '',
      long_description: '',
      price: '',
      discount_percentage: '',
      is_featured: false,
      category: '',
      sizes: [],
      colors: [],
      stock: {},
      images: []
    });
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      short_description: product.short_description,
      long_description: product.long_description,
      price: product.price.toString(),
      discount_percentage: product.discount_percentage.toString(),
      is_featured: product.is_featured,
      category: product.category,
      sizes: product.sizes || [],
      colors: product.colors || [],
      stock: product.stock || {},
      images: product.images || []
    });
    setShowProductModal(true);
  };

  const generateSalesReport = () => {
    const report = `
REPORTE DE VENTAS
================

Fecha de generación: ${new Date().toLocaleDateString()}

RESUMEN DIARIO:
--------------
${salesData.map(sale => `
Fecha: ${sale.date}
Ventas totales: $${sale.total_sales}
Número de órdenes: ${sale.total_orders}
`).join('\n')}

TOTAL GENERAL:
--------------
Ventas totales: $${salesData.reduce((sum, sale) => sum + parseFloat(sale.total_sales), 0).toFixed(2)}
Órdenes totales: ${salesData.reduce((sum, sale) => sum + parseInt(sale.total_orders), 0)}
    `;
    
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte-ventas-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Función para calcular stock total
  const calculateTotalStock = (stock) => {
    if (!stock || typeof stock !== 'object') return 0;
    
    let total = 0;
    Object.values(stock).forEach(sizeStock => {
      if (typeof sizeStock === 'object') {
        Object.values(sizeStock).forEach(quantity => {
          total += parseInt(quantity) || 0;
        });
      }
    });
    return total;
  };

  // Función para generar matriz de stock basada en tallas y colores seleccionados
  const generateStockMatrix = (sizes, colors) => {
    const matrix = {};
    sizes.forEach(size => {
      matrix[size] = {};
      colors.forEach(color => {
        matrix[size][color] = 0;
      });
    });
    return matrix;
  };

  // Función para actualizar stock
  const updateStockValue = (size, color, value) => {
    const newStock = { ...productForm.stock };
    if (!newStock[size]) {
      newStock[size] = {};
    }
    newStock[size][color] = parseInt(value) || 0;
    
    setProductForm(prev => ({
      ...prev,
      stock: newStock
    }));
  };

  // Función para aplicar la misma cantidad a todas las combinaciones
  const applyToAllStock = (quantity) => {
    const newStock = {};
    productForm.sizes.forEach(size => {
      newStock[size] = {};
      productForm.colors.forEach(color => {
        newStock[size][color] = parseInt(quantity) || 0;
      });
    });
    
    setProductForm(prev => ({
      ...prev,
      stock: newStock
    }));
  };

  const tabs = [
    { id: 'products', label: 'Productos', icon: Package },
    { id: 'orders', label: 'Pedidos', icon: ShoppingCart },
    { id: 'sales', label: 'Ventas', icon: TrendingUp },
    { id: 'banners', label: 'Banners', icon: Image }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold gradient-text">Panel de Administración</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-purple-600 transition-colors"
              >
                Ver Tienda
              </button>
              <button
                onClick={logout}
                className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex space-x-1 mb-8">
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-purple-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
            </motion.button>
          ))}
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">Gestión de Productos</h2>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowProductModal(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Agregar Producto</span>
              </motion.button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-lg shadow-md overflow-hidden"
                >
                  <img
                    src={product.images?.[0] || '/api/placeholder/300/200'}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-800">{product.name}</h3>
                      {product.is_featured && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                          Destacado
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{product.short_description}</p>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-lg font-bold text-purple-600">${product.price}</span>
                      <span className="text-sm text-gray-500">{product.category}</span>
                    </div>
                    
                    {/* Información de Stock */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Stock Total:</span>
                        <span className={`text-sm font-bold ${
                          calculateTotalStock(product.stock) > 10 
                            ? 'text-green-600' 
                            : calculateTotalStock(product.stock) > 0 
                              ? 'text-orange-600' 
                              : 'text-red-600'
                        }`}>
                          {calculateTotalStock(product.stock)} unidades
                        </span>
                      </div>
                      {calculateTotalStock(product.stock) <= 5 && calculateTotalStock(product.stock) > 0 && (
                        <div className="mt-1">
                          <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                            Stock Bajo
                          </span>
                        </div>
                      )}
                      {calculateTotalStock(product.stock) === 0 && (
                        <div className="mt-1">
                          <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                            Sin Stock
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => openEditModal(product)}
                        className="flex-1 bg-blue-500 text-white py-2 px-3 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center space-x-1"
                      >
                        <Edit className="w-4 h-4" />
                        <span>Editar</span>
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleDeleteProduct(product.id)}
                        className="bg-red-500 text-white py-2 px-3 rounded-lg hover:bg-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-800">Pedidos Pendientes</h2>
            
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Productos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {order.customer_name} {order.customer_lastname}
                            </div>
                            <div className="text-sm text-gray-500">{order.customer_email}</div>
                            <div className="text-sm text-gray-500">{order.customer_phone}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {order.items.map((item, index) => (
                              <div key={index} className="mb-1">
                                {item.product_name} - {item.size} - {item.color} (x{item.quantity})
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ${order.total_amount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            order.status === 'pendiente' 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2"> {/* Contenedor para los botones */}
                            {order.status === 'pendiente' ? (
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleUpdateOrderStatus(order.id, 'entregado')}
                                className="bg-green-500 text-white px-3 py-1 rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-1"
                              >
                                <Check className="w-4 h-4" />
                                <span>Entregar</span>
                              </motion.button>
                            ) : (
                              <span className="text-green-600 font-semibold">Entregado</span>
                            )}
                            {/* BOTÓN PARA ELIMINAR ORDEN */}
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDeleteOrder(order.id)}
                              className="bg-red-500 text-white px-3 py-1 rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-1"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Eliminar</span>
                            </motion.button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Sales Tab */}
        {activeTab === 'sales' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">Reportes de Ventas</h2>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={generateSalesReport}
                className="btn-primary flex items-center space-x-2"
              >
                <Download className="w-5 h-5" />
                <span>Descargar Reporte</span>
              </motion.button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Ventas Totales</h3>
                <p className="text-3xl font-bold text-purple-600">
                  ${salesData.reduce((sum, sale) => sum + parseFloat(sale.total_sales), 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Órdenes Totales</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {salesData.reduce((sum, sale) => sum + parseInt(sale.total_orders), 0)}
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Promedio por Orden</h3>
                <p className="text-3xl font-bold text-green-600">
                  ${salesData.length > 0 ? (
                    salesData.reduce((sum, sale) => sum + parseFloat(sale.total_sales), 0) / 
                    salesData.reduce((sum, sale) => sum + parseInt(sale.total_orders), 0)
                  ).toFixed(2) : '0.00'}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ventas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Órdenes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Promedio
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {salesData.map((sale, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(sale.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ${sale.total_sales}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {sale.total_orders}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${(parseFloat(sale.total_sales) / parseInt(sale.total_orders)).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        {/* Banners Tab */}
        {activeTab === 'banners' && (
          <div className="space-y-6">
            <BannerManagement />
          </div>
        )}
      </div>

      {/* Product Modal */}
      <AnimatePresence>
        {showProductModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {editingProduct ? 'Editar Producto' : 'Agregar Producto'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowProductModal(false);
                      setEditingProduct(null);
                      resetProductForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleProductSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre del Producto
                      </label>
                      <input
                        type="text"
                        value={productForm.name}
                        onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Categoría
                      </label>
                      <select
                        value={productForm.category}
                        onChange={(e) => setProductForm(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        required
                      >
                        <option value="">Seleccionar categoría</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Precio
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={productForm.price}
                        onChange={(e) => setProductForm(prev => ({ ...prev, price: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descuento (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={productForm.discount_percentage}
                        onChange={(e) => setProductForm(prev => ({ ...prev, discount_percentage: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción Corta
                    </label>
                    <input
                      type="text"
                      value={productForm.short_description}
                      onChange={(e) => setProductForm(prev => ({ ...prev, short_description: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción Larga
                    </label>
                    <textarea
                      value={productForm.long_description}
                      onChange={(e) => setProductForm(prev => ({ ...prev, long_description: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      rows="4"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tallas
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {sizes.map(size => (
                          <label key={size} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={productForm.sizes.includes(size)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setProductForm(prev => {
                                    const newSizes = [...prev.sizes, size];
                                    const newStock = generateStockMatrix(newSizes, prev.colors);
                                    // Mantener valores existentes si ya existen
                                    Object.keys(prev.stock).forEach(existingSize => {
                                      if (newStock[existingSize]) {
                                        Object.keys(prev.stock[existingSize]).forEach(existingColor => {
                                          if (newStock[existingSize][existingColor] !== undefined) {
                                            newStock[existingSize][existingColor] = prev.stock[existingSize][existingColor];
                                          }
                                        });
                                      }
                                    });
                                    
                                    return {
                                      ...prev,
                                      sizes: newSizes,
                                      stock: newStock
                                    };
                                  });
                                } else {
                                  setProductForm(prev => {
                                    const newSizes = prev.sizes.filter(s => s !== size);
                                    const newStock = generateStockMatrix(newSizes, prev.colors);
                                    // Mantener valores existentes si ya existen
                                    Object.keys(prev.stock).forEach(existingSize => {
                                      if (newStock[existingSize]) {
                                        Object.keys(prev.stock[existingSize]).forEach(existingColor => {
                                          if (newStock[existingSize][existingColor] !== undefined) {
                                            newStock[existingSize][existingColor] = prev.stock[existingSize][existingColor];
                                          }
                                        });
                                      }
                                    });
                                    
                                    return {
                                      ...prev,
                                      sizes: newSizes,
                                      stock: newStock
                                    };
                                  });
                                }
                              }}
                              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <span className="text-sm">{size}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Colores
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {colors.map(color => (
                          <label key={color} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={productForm.colors.includes(color)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setProductForm(prev => {
                                    const newColors = [...prev.colors, color];
                                    const newStock = generateStockMatrix(prev.sizes, newColors);
                                    // Mantener valores existentes si ya existen
                                    Object.keys(prev.stock).forEach(existingSize => {
                                      if (newStock[existingSize]) {
                                        Object.keys(prev.stock[existingSize]).forEach(existingColor => {
                                          if (newStock[existingSize][existingColor] !== undefined) {
                                            newStock[existingSize][existingColor] = prev.stock[existingSize][existingColor];
                                          }
                                        });
                                      }
                                    });
                                    
                                    return {
                                      ...prev,
                                      colors: newColors,
                                      stock: newStock
                                    };
                                  });
                                } else {
                                  setProductForm(prev => {
                                    const newColors = prev.colors.filter(c => c !== color);
                                    const newStock = generateStockMatrix(prev.sizes, newColors);
                                    // Mantener valores existentes si ya existen
                                    Object.keys(prev.stock).forEach(existingSize => {
                                      if (newStock[existingSize]) {
                                        Object.keys(prev.stock[existingSize]).forEach(existingColor => {
                                          if (newStock[existingSize][existingColor] !== undefined) {
                                            newStock[existingSize][existingColor] = prev.stock[existingSize][existingColor];
                                          }
                                        });
                                      }
                                    });
                                    
                                    return {
                                      ...prev,
                                      colors: newColors,
                                      stock: newStock
                                    };
                                  });
                                }
                              }}
                              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <span className="text-sm">{color}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Imágenes
                    </label>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    {productForm.images.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {productForm.images.map((image, index) => (
                          <div key={index} className="relative">
                            <img
                              src={image}
                              alt={`Imagen ${index + 1}`}
                              className="w-20 h-20 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setProductForm(prev => ({
                                  ...prev,
                                  images: prev.images.filter((_, i) => i !== index)
                                }));
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Gestión de Stock */}
                  {productForm.sizes.length > 0 && productForm.colors.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">
                          Gestión de Stock
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            placeholder="Cantidad"
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value) {
                                applyToAllStock(value);
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const input = document.querySelector('input[placeholder="Cantidad"]');
                              if (input && input.value) {
                                applyToAllStock(input.value);
                              }
                            }}
                            className="px-3 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                          >
                            Aplicar a Todo
                          </button>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {productForm.sizes.map(size => (
                            <div key={size} className="bg-white p-3 rounded-lg border">
                              <h4 className="font-medium text-gray-800 mb-2 text-center">
                                Talla {size}
                              </h4>
                              <div className="space-y-2">
                                {productForm.colors.map(color => (
                                  <div key={`${size}-${color}`} className="flex items-center space-x-2">
                                    <span className="text-sm text-gray-600 flex-1">
                                      {color}:
                                    </span>
                                    <input
                                      type="number"
                                      min="0"
                                      value={productForm.stock[size]?.[color] || 0}
                                      onChange={(e) => updateStockValue(size, color, e.target.value)}
                                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-blue-800">
                              Stock Total:
                            </span>
                            <span className="text-lg font-bold text-blue-600">
                              {calculateTotalStock(productForm.stock)} unidades
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={productForm.is_featured}
                        onChange={(e) => setProductForm(prev => ({ ...prev, is_featured: e.target.checked }))}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Producto Destacado</span>
                    </label>
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowProductModal(false);
                        setEditingProduct(null);
                        resetProductForm();
                      }}
                      className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      {editingProduct ? 'Actualizar' : 'Crear'} Producto
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;
